import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["button", "children", "className", "disabled", "external", "externalLinkText", "href", "icon", "iconPos", "inline", "loading", "nonInteractive", "rel", "role", "size", "tabIndex", "target"],
  _excluded2 = ["fwdRef"],
  _excluded3 = ["fwdRef", "onClick", "onKeyDown"];
import React, { createContext, forwardRef, useCallback, useContext } from 'react';
import { IconLinkExternal, IconLinkInternal } from '@op/opds.opux-icons/navigation';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { useLocale } from '../common/locale';
import messages from './_messages';
import Loading from '../loading/loading';
const isAnchor = props => props.button === undefined ? true : !props.button;
const isAnchorElement = (props, button, disabled) => {
  // <a disabled> isn't valid HTML so always render button when disabled
  if (disabled) {
    return false;
  } else if (button === undefined) {
    return true;
  } else {
    return !button;
  }
};
const isButtonElement = (props, button, disabled) => {
  if (disabled) {
    return true;
  } else if (button === undefined) {
    return false;
  } else {
    return button;
  }
};

/**
 * _Interactive text that links users to somewhere else or performs an immediate action._
 *
 * ***
 *
 * The component enables creating plain text links and actions. It has many
 * similarities with the Button and can often replace one.
 *
 * The children of the component should be text. Icons etc. are included via
 * props. Renders either `<a>` or `<button>` HTML element. Additional props (including `ref`)
 * will be forwarded to the wrapping HTML element.
 *
 * Also exposes `Action.Context` to allow for a
 * [context provider](https://reactjs.org/docs/context.html#contextprovider) to
 * supply some of the component's props (`button`, `external` and `inline`)
 */
export const Action = props => {
  const {
      button = false,
      children,
      className,
      disabled,
      external,
      externalLinkText,
      href,
      icon: propIcon,
      iconPos: propIconPos = 'right',
      inline,
      loading,
      nonInteractive,
      rel: propRel,
      role: propRole,
      size = 'default',
      tabIndex: propTabIndex = 0,
      target: propTarget
    } = props,
    actionProps = _objectWithoutPropertiesLoose(props, _excluded);
  const lc = useLocale();
  const Icon = (() => {
    if (external) {
      return IconLinkExternal;
    } else if (propIcon) {
      return propIcon;
    } else {
      return IconLinkInternal;
    }
  })();
  const iconPos = external ? 'right' : propIconPos;
  const iconSize = size === 'medium' || size === 'large' ? 'default' : 'small';
  const iconColor = !inline && disabled ? 'disabled' : 'action';
  const label = external ? externalLinkText != null ? externalLinkText : messages.externalLink[lc] : undefined;
  const rel = href && !propRel ? 'noopener' : propRel;
  const role = !href && !propRole && !button ? 'button' : propRole;
  const tabIndex = disabled || loading || nonInteractive ? -1 : propTabIndex;
  const target = external && !propTarget ? '_blank' : propTarget;
  const cn = buildVersionedClassNames('ds-action', className, {
    'ds-action--disabled': !inline && disabled,
    'ds-action--external': external,
    'ds-action--icon-left': !external && !inline && iconPos === 'left',
    'ds-action--inline': inline,
    ["ds-action--" + size]: !inline && size !== 'default',
    'ds-action--non-interactive': nonInteractive || loading
  });
  const Indicator = useCallback(() => {
    const iconOrNull = external || !inline ? /*#__PURE__*/React.createElement(Icon, {
      className: buildVersionedClass('ds-action__icon'),
      color: iconColor,
      label: label,
      size: iconSize
    }) : null;
    return loading && !disabled && !external && !inline ? /*#__PURE__*/React.createElement(Loading, {
      className: buildVersionedClass('ds-action__icon'),
      size: iconSize
    }) : iconOrNull;
  }, [Icon, disabled, external, iconColor, iconSize, inline, label, loading]);
  if (isAnchorElement(actionProps, button, disabled)) {
    const {
        fwdRef
      } = actionProps,
      otherAnchorProps = _objectWithoutPropertiesLoose(actionProps, _excluded2);
    const hasOnClick = 'onClick' in otherAnchorProps && otherAnchorProps.onClick;
    function onKeyDownHandler(event) {
      const isTriggerKey = event.key === ' ' || event.key === 'Enter';
      if (hasOnClick && isTriggerKey) {
        event.preventDefault();
        otherAnchorProps.onClick();
      }
    }
    return /*#__PURE__*/React.createElement("a", _extends({
      "aria-busy": loading,
      className: cn,
      href: href
    }, !button && !href && hasOnClick ? {
      onKeyDown: event => onKeyDownHandler(event)
    } : {}, {
      ref: fwdRef,
      rel: rel,
      role: role,
      tabIndex: tabIndex,
      target: target
    }, otherAnchorProps), inline && !external ? children : /*#__PURE__*/React.createElement(React.Fragment, null, iconPos === 'left' && /*#__PURE__*/React.createElement(Indicator, null), /*#__PURE__*/React.createElement("span", {
      className: buildVersionedClass('ds-action__label')
    }, children), "\u200D", iconPos === 'right' && /*#__PURE__*/React.createElement(Indicator, null)));
  } else if (isButtonElement(actionProps, button, disabled)) {
    const {
        fwdRef,
        onClick,
        onKeyDown: propKeyDown
      } = actionProps,
      otherButtonProps = _objectWithoutPropertiesLoose(actionProps, _excluded3);
    const onKeyDown = onClick && propKeyDown ? propKeyDown : undefined;
    return /*#__PURE__*/React.createElement("button", _extends({
      "aria-busy": loading,
      className: cn,
      disabled: disabled,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: fwdRef,
      role: role,
      tabIndex: tabIndex
    }, otherButtonProps), iconPos === 'left' && /*#__PURE__*/React.createElement(Indicator, null), /*#__PURE__*/React.createElement("span", {
      className: buildVersionedClass('ds-action__label')
    }, children), "\u200D", iconPos === 'right' && /*#__PURE__*/React.createElement(Indicator, null));
  } else {
    return null;
  }
};
export const ActionContext = /*#__PURE__*/createContext({
  external: null,
  inline: null
});

// The if...else is for typing the refs correctly
const Fwd = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    external,
    inline
  } = useContext(ActionContext);
  const ctxProps = {
    external: null,
    inline: null
  };
  if (typeof external === 'boolean') {
    ctxProps.external = external;
  }
  if (typeof inline === 'boolean') {
    ctxProps.inline = inline;
  }
  if (isAnchor(props)) {
    return /*#__PURE__*/React.createElement(Action, _extends({}, ctxProps, props, {
      button: false,
      fwdRef: ref
    }));
  } else {
    return /*#__PURE__*/React.createElement(Action, _extends({}, ctxProps, props, {
      button: true,
      fwdRef: ref
    }));
  }
});
Fwd.displayName = 'Action';
export default Fwd;